import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowLeft, ArrowRight, MessageCircle, CheckCircle2, RotateCcw } from 'lucide-react';

const WHATSAPP_NUMBER = "+41779938989";

const wizardSteps = [
  {
    id: 1,
    question: 'Worum geht es?',
    options: [
      { value: 'personal', label: 'Persönliche Sicherheit' },
      { value: 'event', label: 'Veranstaltung & Publikum' },
      { value: 'travel', label: 'Reise ins Ausland' },
      { value: 'property', label: 'Objektschutz' },
      { value: 'access', label: 'Zutritt & Empfang' },
    ]
  },
  {
    id: 2,
    question: 'Wo findet es statt?',
    options: [
      { value: 'switzerland', label: 'Schweiz' },
      { value: 'germany', label: 'Deutschland' },
      { value: 'europe', label: 'Europa' },
      { value: 'international', label: 'International' },
    ]
  },
  {
    id: 3,
    question: 'Wann ungefähr?',
    options: [
      { value: 'days', label: 'In den nächsten Tagen' },
      { value: 'weeks', label: 'In 1-4 Wochen' },
      { value: 'months', label: 'In 1-3 Monaten' },
      { value: 'ongoing', label: 'Langfristig / laufend' },
    ]
  },
  {
    id: 4,
    question: 'Gibt es konkrete Bedrohungen?',
    options: [
      { value: 'acute', label: 'Ja, akut' },
      { value: 'suspected', label: 'Ja, vermutet' },
      { value: 'preventive', label: 'Nein, präventiv' },
      { value: 'private', label: 'Möchte ich nicht sagen' },
    ]
  },
  {
    id: 5,
    question: 'Für wie viele Personen?',
    options: [
      { value: 'single', label: 'Einzelperson' },
      { value: 'small', label: 'Kleine Gruppe (2-5)' },
      { value: 'large', label: 'Grössere Gruppe (5+)' },
      { value: 'org', label: 'Organisation / Firma' },
    ]
  },
];

const getServiceRecommendation = (answers) => {
  const serviceType = answers[0];

  const recommendations = {
    personal: {
      title: 'Persönlicher Schutz',
      subtitle: 'individuell auf Sie zugeschnitten',
      benefits: [
        'Diskrete Begleitung durch erfahrene Fachkräfte',
        'Flexible Anpassung an Ihren Alltag',
        'Schnelle Verfügbarkeit bei Bedarf',
      ]
    },
    event: {
      title: 'Veranstaltung & Publikum',
      subtitle: 'für Ihren Event',
      benefits: [
        'Professionelles Einlassmanagement & Ticketkontrollen',
        'Crowd Management für sichere Besucherströme',
        'VIP-Zonen-Schutz und Backstage-Sicherheit',
      ]
    },
    travel: {
      title: 'Reiseschutz',
      subtitle: 'für Ihre sichere Reise',
      benefits: [
        'Vorausplanung und Risikoanalyse',
        'Begleitung durch erfahrene Spezialisten',
        'Lokale Netzwerke weltweit',
      ]
    },
    property: {
      title: 'Objektschutz',
      subtitle: 'für Ihre Immobilie',
      benefits: [
        'Rund-um-die-Uhr Überwachung',
        'Zugangskontrollen und Patrouillen',
        'Schnelle Alarmintervention',
      ]
    },
    access: {
      title: 'Zutritt & Empfang',
      subtitle: 'professioneller Empfangsdienst',
      benefits: [
        'Empfangs- und Pfortendienst im Concierge-Style',
        'Besuchermanagement mit Badge-System',
        'Schlüsselverwaltung und Kontrollgänge',
      ]
    },
  };

  return recommendations[serviceType] || recommendations.personal;
};

const generateWhatsAppMessage = (answers) => {
  const serviceLabels = {
    personal: 'Persönliche Sicherheit',
    event: 'Veranstaltung & Publikum',
    travel: 'Reiseschutz',
    property: 'Objektschutz',
    access: 'Zutritt & Empfang',
  };

  const locationLabels = {
    switzerland: 'Schweiz',
    germany: 'Deutschland',
    europe: 'Europa',
    international: 'International',
  };

  const timeLabels = {
    days: 'In den nächsten Tagen',
    weeks: 'In 1-4 Wochen',
    months: 'In 1-3 Monaten',
    ongoing: 'Langfristig',
  };

  const peopleLabels = {
    single: 'Einzelperson',
    small: 'Kleine Gruppe (2-5)',
    large: 'Grössere Gruppe (5+)',
    org: 'Organisation/Firma',
  };

  const message = `Guten Tag,

ich interessiere mich für Ihre Sicherheitsdienstleistungen.

📋 Meine Anfrage:
• Bedarf: ${serviceLabels[answers[0]] || 'Nicht angegeben'}
• Ort: ${locationLabels[answers[1]] || 'Nicht angegeben'}
• Zeitraum: ${timeLabels[answers[2]] || 'Nicht angegeben'}
• Personenanzahl: ${peopleLabels[answers[4]] || 'Nicht angegeben'}

Ich freue mich auf Ihre Rückmeldung.`;

  return encodeURIComponent(message);
};

export const RequestWizard = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isComplete, setIsComplete] = useState(false);

  const progress = ((currentStep + 1) / wizardSteps.length) * 100;

  const handleSelect = (value) => {
    setAnswers({ ...answers, [currentStep]: value });

    if (currentStep < wizardSteps.length - 1) {
      setTimeout(() => setCurrentStep(currentStep + 1), 300);
    } else {
      setTimeout(() => setIsComplete(true), 300);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setAnswers({});
    setIsComplete(false);
  };

  const handleWhatsApp = () => {
    const message = generateWhatsAppMessage(answers);
    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}?text=${message}`, '_blank');
    onClose();
    handleReset();
  };

  const handleClose = () => {
    onClose();
    setTimeout(handleReset, 300);
  };

  const recommendation = isComplete ? getServiceRecommendation(answers) : null;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0B0F19]/95 backdrop-blur-sm"
        data-testid="request-wizard-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-full max-w-2xl bg-[#111625] border border-[#1E293B] rounded-sm overflow-hidden"
          data-testid="request-wizard-modal"
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 text-[#64748B] hover:text-[#F1F5F9] transition-colors z-10"
            data-testid="wizard-close-btn"
          >
            <X size={24} />
          </button>

          {/* Header */}
          <div className="p-6 md:p-8 pb-0 text-center">
            <h2 className="text-2xl md:text-3xl font-semibold text-[#F1F5F9] font-['DM_Sans']">
              60-Sekunden Anfrage
            </h2>
            <p className="text-[#94A3B8] mt-2 text-sm">
              Fünf Klicks – und wir wissen, wie wir helfen können.
            </p>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8">
            {/* Progress */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2 text-sm">
                <span className="text-[#94A3B8]">
                  {isComplete ? 'Fertig!' : `Schritt ${currentStep + 1} von ${wizardSteps.length}`}
                </span>
                <span className="text-[#D4AF37]">{Math.round(isComplete ? 100 : progress)}%</span>
              </div>
              <div className="progress-bar">
                <motion.div
                  className="progress-fill"
                  initial={{ width: 0 }}
                  animate={{ width: isComplete ? '100%' : `${progress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>

            <AnimatePresence mode="wait">
              {!isComplete ? (
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Question */}
                  <h3 className="text-xl md:text-2xl font-semibold text-[#F1F5F9] mb-6 font-['DM_Sans']">
                    {wizardSteps[currentStep].question}
                  </h3>

                  {/* Options */}
                  <div className="grid gap-3">
                    {wizardSteps[currentStep].options.map((option, index) => (
                      <button
                        key={option.value}
                        onClick={() => handleSelect(option.value)}
                        className={`wizard-option ${answers[currentStep] === option.value ? 'selected' : ''}`}
                        data-testid={`wizard-step-${currentStep + 1}-option-${index + 1}`}
                      >
                        <span className="text-[#F1F5F9] text-left">{option.label}</span>
                        <ArrowRight size={18} className="text-[#64748B] group-hover:text-[#D4AF37] transition-colors" />
                      </button>
                    ))}
                  </div>

                  {/* Back Button */}
                  {currentStep > 0 && (
                    <button
                      onClick={handleBack}
                      className="mt-6 flex items-center gap-2 text-[#94A3B8] hover:text-[#F1F5F9] transition-colors text-sm"
                      data-testid="wizard-back-btn"
                    >
                      <ArrowLeft size={16} />
                      Zurück
                    </button>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-center"
                >
                  {/* Success Icon */}
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full border-2 border-[#D4AF37] flex items-center justify-center">
                    <CheckCircle2 size={32} className="text-[#D4AF37]" />
                  </div>

                  {/* Recommendation */}
                  <h3 className="text-xl md:text-2xl font-semibold text-[#F1F5F9] mb-2 font-['DM_Sans']">
                    {recommendation.title} – {recommendation.subtitle}
                  </h3>

                  {/* Benefits */}
                  <div className="mt-6 p-4 bg-[#0B0F19] border border-[#1E293B] rounded-sm text-left">
                    <p className="text-[#D4AF37] text-sm font-medium mb-3">Warum das passt:</p>
                    <ul className="space-y-2">
                      {recommendation.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2 text-[#94A3B8] text-sm">
                          <CheckCircle2 size={16} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTAs */}
                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleWhatsApp}
                      className="btn-primary flex-1 flex items-center justify-center gap-2"
                      data-testid="wizard-whatsapp-btn"
                    >
                      <MessageCircle size={18} />
                      So per WhatsApp anfragen
                    </button>
                    <button
                      onClick={handleReset}
                      className="btn-secondary flex items-center justify-center gap-2"
                      data-testid="wizard-reset-btn"
                    >
                      <RotateCcw size={18} />
                      Nochmals starten
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
